// CLASSIC LOOK (v1.70): pre-Floodlight copy from ea44aec, used by
// screens/classic/CommunityScreen.jsx. Keep behaviour in step with
// components/community/ChannelContent.jsx (the Floodlight version).
import { useState, useRef, useEffect, useCallback, useMemo, memo } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Modal,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  Linking,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { Image } from "react-native";

import { useAuth } from "../../../context/AuthContext";
import { sendMessage, subscribeToMessages } from "../../../services/communityService";
import EmbedPreview from "./EmbedPreview";
import SharedBetCard from "./SharedBetCard";
import Appear from "../../motion/Appear";
import PressableScale from "../../motion/PressableScale";
import { useNavigation } from "../../../context/NavigationContext";
import { takePendingShare } from "../../../utils/communityShare";

// ─── Palette ──────────────────────────────────────────────────────────────────

const BRAND       = "#A259FF";
const TEXT_PRI    = "rgba(255,255,255,0.90)";
const TEXT_SEC    = "rgba(255,255,255,0.60)";
const TEXT_TER    = "rgba(255,255,255,0.35)";
const BORDER      = "rgba(255,255,255,0.08)";
const CARD_BG     = "rgba(255,255,255,0.04)";
const INPUT_BG    = "rgba(255,255,255,0.07)";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "#A259FF", "#4ade80", "#f87171", "#fbbf24",
  "#60a5fa", "#f472b6", "#34d399", "#fb923c",
];

function avatarColor(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function initials(str = "") {
  const parts = str.replace(/\d+$/, "").match(/[A-Z][a-z]*/g) || [str];
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase() || str.slice(0, 2).toUpperCase();
}

function formatTime(createdAt) {
  if (!createdAt) return "";
  const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDate(createdAt) {
  if (!createdAt) return "";
  const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function dayKey(createdAt) {
  if (!createdAt) return "";
  const d = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function minuteKey(createdAt) {
  if (!createdAt) return "";
  const d = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}-${d.getMinutes()}`;
}

// ─── URL helpers ─────────────────────────────────────────────────────────────

const URL_RE = /https?:\/\/[^\s<>"]+/g;

function parseTextSegments(text) {
  const segments = [];
  let lastIndex = 0;
  for (const match of text.matchAll(URL_RE)) {
    if (match.index > lastIndex)
      segments.push({ kind: "text", value: text.slice(lastIndex, match.index) });
    segments.push({ kind: "url", value: match[0] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length)
    segments.push({ kind: "text", value: text.slice(lastIndex) });
  return segments;
}

// ─── Day separator ────────────────────────────────────────────────────────────

const DaySeparator = memo(function DaySeparator({ createdAt }) {
  return (
    <View style={styles.daySep}>
      <View style={styles.daySepLine} />
      <Text style={styles.daySepText}>{formatDate(createdAt)}</Text>
      <View style={styles.daySepLine} />
    </View>
  );
});

// ─── Message row ─────────────────────────────────────────────────────────────

const MessageRow = memo(function MessageRow({ msg, showHeader, showTimestamp, isOwn, onRunQuery, index }) {
  const displayName = msg.username || msg.displayName || "Unknown";
  const color = avatarColor(displayName);
  const abbr = initials(displayName);

  const segments = useMemo(() => parseTextSegments(msg.text ?? ""), [msg.text]);
  const embedUrl = useMemo(
    () => segments.find((s) => s.kind === "url")?.value ?? msg.videoUrl ?? null,
    [segments, msg.videoUrl],
  );

  return (
    <Appear index={index} distance={6} style={[styles.msgRow, showHeader && styles.msgRowFirst]} {...(Platform.OS === "web" ? { dataSet: { msg: "" } } : {})}>
      {/* Left gutter: avatar or time stamp */}
      {showHeader ? (
        msg.photoURL ? (
          <Image source={{ uri: msg.photoURL }} style={styles.msgAvatar} accessibilityLabel={`${displayName} avatar`} />
        ) : (
        <View style={[styles.msgAvatar, { backgroundColor: color }]}>
          <Text style={styles.msgAvatarText}>{abbr}</Text>
        </View>
        )
      ) : showTimestamp ? (
        <Text style={styles.msgGutterTime}>{formatTime(msg.createdAt)}</Text>
      ) : (
        <View style={styles.msgGutter} />
      )}

      {/* Content */}
      <View style={styles.msgContent}>
        {showHeader && (
          <View style={styles.msgHeader}>
            <Text style={[styles.msgUsername, { color }]}>{displayName}</Text>
            <Text style={styles.msgTime}>{formatTime(msg.createdAt)}</Text>
          </View>
        )}
        {!!msg.text && <Text style={styles.msgText} selectable>
          {segments.map((seg, i) =>
            seg.kind === "url" ? (
              <Text
                key={i}
                style={styles.msgLink}
                onPress={() => Linking.openURL(seg.value).catch(() => {})}
              >
                {seg.value}
              </Text>
            ) : (
              <Text key={i}>{seg.value}</Text>
            ),
          )}
        </Text>}
        {!!embedUrl && <EmbedPreview url={embedUrl} />}
        {!!msg.bet && <SharedBetCard bet={msg.bet} onRun={onRunQuery} />}
        {msg.needsReview && isOwn && <Text style={styles.msgReview}>Visible to you · waiting for review</Text>}
      </View>
    </Appear>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

export default function ChannelContent({ communityId, categoryId, channelId, channelName, channelTopic, compact, onOpenChannels, onOpenMembers, onlineCount }) {
  const { user } = useAuth();
  const { navigateTo } = useNavigation();
  const [pageSize, setPageSize] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pendingBet, setPendingBet] = useState(() => takePendingShare());
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendNotice, setSendNotice] = useState(null);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const scrollRef = useRef(null);

  const resolvedName = channelName ?? channelId ?? "";
  const canSend = !!(communityId && categoryId && channelId);

  useEffect(() => {
    if (!communityId || !categoryId || !channelId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    const unsub = subscribeToMessages(communityId, categoryId, channelId, (msgs, meta) => {
      setMessages(msgs);
      setHasMore(!!meta?.hasMore);
      setLoading(false);
    }, pageSize, (err) => setLoadError(err));
    return unsub;
  }, [communityId, categoryId, channelId, pageSize]);

  useEffect(() => { setPageSize(50); setSearchText(""); }, [channelId]);

  const stickToBottom = useRef(true);
  useEffect(() => {
    if (stickToBottom.current) setTimeout(() => scrollRef.current?.scrollToEnd({ animated: false }), 50);
  }, [messages]);

  const runQuery = useCallback((q) => navigateTo("SgpFlow", { payload: { query: q } }), [navigateTo]);

  const handleAttachVideoUrl = useCallback(() => {
    setVideoUrl("");
    setVideoModalVisible(true);
  }, []);

  const handleConfirmVideoUrl = useCallback(() => {
    if (videoUrl.trim())
      setMessage((prev) => (prev ? `${prev} ${videoUrl.trim()}` : videoUrl.trim()));
    setVideoModalVisible(false);
  }, [videoUrl]);

  const handleSend = useCallback(async () => {
    if ((!message.trim() && !pendingBet) || !canSend || sending || !user) return;
    const text = message.trim();
    setSendError(null);
    setSendNotice(null);
    setMessage("");
    setSending(true);

    const result = await sendMessage(communityId, categoryId, channelId, {
      uid: user?.uid ?? "anonymous",
      username: user?.username ?? (user?.email ? user.email.split("@")[0] : null) ?? user?.displayName ?? null,
      displayName: user?.displayName ?? user?.username ?? null,
      photoURL: user?.photoURL ?? user?.profilePhoto ?? null,
      text,
      ...(pendingBet ? { bet: pendingBet } : {}),
    });

    if (result.blocked) {
      setMessage(text);
      setSendError("Message blocked: violates community guidelines.");
    } else if (result.error) {
      setSendError("Failed to send. Please try again.");
    } else {
      stickToBottom.current = true;
      setPendingBet(null);
      if (result.flagged) setSendNotice("Message sent and queued for review.");
    }
    setSending(false);
  }, [message, communityId, categoryId, channelId, user, canSend, sending, pendingBet]);

  // Build message list with day separators
  const visibleMessages = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter((m) => [m.text, m.username, m.displayName, m.bet?.title, ...(m.bet?.legs || []).map((l) => l.label)].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [messages, searchText]);

  const renderedItems = useMemo(() => {
    const messages = visibleMessages;
    if (!messages.length) return [];
    const items = [];
    let lastDay = null;
    messages.forEach((msg, idx) => {
      const day = dayKey(msg.createdAt);
      if (day !== lastDay) {
        items.push({ type: "day", key: `day-${day}`, createdAt: msg.createdAt });
        lastDay = day;
      }
      const prev = messages[idx - 1];
      const newDay = idx === 0 || dayKey(prev?.createdAt) !== day;
      const showHeader = newDay || idx === 0 || prev?.uid !== msg.uid;
      const showTimestamp = !showHeader && minuteKey(prev?.createdAt) !== minuteKey(msg.createdAt);
      items.push({ type: "msg", key: msg.id, msg, showHeader, showTimestamp, isOwn: msg.uid === user?.uid });
    });
    return items;
  }, [visibleMessages, user?.uid]);

  const canSubmit = (message.trim() || pendingBet) && canSend && !sending && !!user;

  return (
    <View style={styles.root}>
      {/* Channel header */}
      <View style={styles.header}>
        <View style={[styles.headerLeft, { flex: 1, minWidth: 0 }]}>
          {compact && onOpenChannels ? (
            <PressableScale onPress={onOpenChannels} style={styles.headerBtn} accessibilityRole="button" accessibilityLabel="Open channel list">
              <Ionicons name="menu" size={20} color={TEXT_SEC} />
            </PressableScale>
          ) : null}
          <Text style={styles.headerHash} aria-hidden>#</Text>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.headerName} numberOfLines={1} accessibilityRole="header">{resolvedName}</Text>
            {!!channelTopic && !compact && <Text style={styles.headerTopic} numberOfLines={1}>{channelTopic}</Text>}
          </View>
        </View>
        <View style={styles.headerActions}>
          <PressableScale onPress={() => { setSearchOpen((o) => !o); if (searchOpen) setSearchText(""); }} style={[styles.headerBtn, searchOpen && styles.headerBtnOn]} accessibilityRole="button" aria-selected={!!searchOpen} accessibilityLabel={searchOpen ? "Close search" : "Search this channel"}>
            <Ionicons name={searchOpen ? "close" : "search-outline"} size={18} color={searchOpen ? TEXT_PRI : TEXT_TER} />
          </PressableScale>
          {onOpenMembers ? (
            <PressableScale onPress={onOpenMembers} style={styles.headerBtn} accessibilityRole="button" accessibilityLabel="Show members">
              <Ionicons name="people-outline" size={18} color={TEXT_TER} />
              {onlineCount > 0 && <View style={styles.headerDot} />}
            </PressableScale>
          ) : null}
        </View>
      </View>
      {searchOpen && (
        <View style={styles.searchBar}>
          <Ionicons name="search-outline" size={14} color={TEXT_TER} />
          <TextInput value={searchText} onChangeText={setSearchText} placeholder={`Search #${resolvedName}`} placeholderTextColor={TEXT_TER} style={styles.searchInput} autoFocus accessibilityLabel="Search messages" />
          {!!searchText && <Text style={styles.searchCount}>{visibleMessages.length} found</Text>}
        </View>
      )}

      {/* Messages */}
      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator color={BRAND} size="small" />
        </View>
      ) : (
        <ScrollView
          ref={scrollRef}
          style={styles.msgList}
          contentContainerStyle={styles.msgListInner}
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={100}
          onScroll={(e) => {
            const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
            stickToBottom.current = contentSize.height - contentOffset.y - layoutMeasurement.height < 120;
          }}
          onContentSizeChange={() => { if (stickToBottom.current) scrollRef.current?.scrollToEnd({ animated: false }); }}
        >
          {hasMore && !searchText && (
            <PressableScale onPress={() => { stickToBottom.current = false; setPageSize((n) => n + 50); }} style={styles.loadMore} accessibilityRole="button">
              <Text style={styles.loadMoreText}>Load earlier messages</Text>
            </PressableScale>
          )}
          {loadError ? (
            <View style={styles.emptyCentered}>
              <Ionicons name="cloud-offline-outline" size={28} color={TEXT_TER} />
              <Text style={styles.emptyTitle}>Couldn't load #{resolvedName}</Text>
              <Text style={styles.emptyBody}>{user ? "Check your connection and try again." : "Sign in to read the conversation."}</Text>
            </View>
          ) : renderedItems.length === 0 ? (
            <View style={styles.emptyCentered}>
              <View style={styles.emptyIcon}>
                <Text style={styles.emptyHash}>#</Text>
              </View>
              <Text style={styles.emptyTitle}>{searchText ? "No messages match" : `Welcome to #${resolvedName}`}</Text>
              <Text style={styles.emptyBody}>{searchText ? "Try another word." : "This is the start of the channel. Share a bet from your bet slip or say hello."}</Text>
            </View>
          ) : (
            renderedItems.map((item) =>
              item.type === "day" ? (
                <DaySeparator key={item.key} createdAt={item.createdAt} />
              ) : (
                <MessageRow
                  key={item.key}
                  msg={item.msg}
                  showHeader={item.showHeader}
                  showTimestamp={item.showTimestamp}
                  isOwn={item.isOwn}
                  onRunQuery={runQuery}
                  index={0}
                />
              ),
            )
          )}
        </ScrollView>
      )}

      {/* Notices */}
      {!!sendError && (
        <View style={styles.noticeBar} accessibilityLiveRegion="polite">
          <Ionicons name="alert-circle-outline" size={13} color="#f87171" style={{ marginRight: 6 }} />
          <Text style={styles.noticeTextError}>{sendError}</Text>
        </View>
      )}
      {!!sendNotice && (
        <View style={styles.noticeBar} accessibilityLiveRegion="polite">
          <Ionicons name="checkmark-circle-outline" size={13} color="#4ade80" style={{ marginRight: 6 }} />
          <Text style={styles.noticeTextOk}>{sendNotice}</Text>
        </View>
      )}

      {/* Chat input bar */}
      {!!pendingBet && (
        <View style={styles.pendingBet}>
          <View style={{ flex: 1 }}>
            <Text style={styles.pendingLabel}>Sharing</Text>
            <SharedBetCard bet={pendingBet} compact />
          </View>
          <PressableScale onPress={() => setPendingBet(null)} style={styles.headerBtn} accessibilityRole="button" accessibilityLabel="Remove the shared bet">
            <Ionicons name="close" size={16} color={TEXT_SEC} />
          </PressableScale>
        </View>
      )}
      {!user ? (
        <View style={styles.signInBar}>
          <Text style={styles.signInText}>Sign in to join the conversation and share bets.</Text>
          <PressableScale onPress={() => navigateTo("Login", { redirectAfterLogin: "Community" })} style={styles.signInBtn} accessibilityRole="button">
            <Text style={styles.signInBtnText}>Sign in</Text>
          </PressableScale>
        </View>
      ) : (
      <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined}>
        <View style={styles.chatBar}>

          {/* Input area */}
          <View style={styles.inputWrap}>
            <TextInput
              style={styles.textInput}
              value={message}
              onChangeText={(val) => {
                setMessage(val);
                if (sendError) setSendError(null);
                if (sendNotice) setSendNotice(null);
              }}
              placeholder={pendingBet ? "Add a note (optional)" : `Message #${resolvedName}`}
              accessibilityLabel={`Message #${resolvedName}`}
              placeholderTextColor={TEXT_TER}
              multiline
              maxLength={2000}
              onKeyPress={Platform.OS === "web" ? (e) => { if (e.nativeEvent.key === "Enter" && !e.nativeEvent.shiftKey) { e.preventDefault?.(); handleSend(); } } : undefined}
            />
            {/* Inline action row inside input */}
            <View style={styles.inputActions}>
              <TouchableOpacity onPress={handleAttachVideoUrl} hitSlop={8} style={styles.inputActionBtn} accessibilityRole="button" accessibilityLabel="Attach a video link">
                <Ionicons name="play-circle-outline" size={18} color={TEXT_TER} />
              </TouchableOpacity>
            </View>
          </View>

          {/* Send button */}
          <TouchableOpacity
            onPress={handleSend}
            style={[styles.sendBtn, canSubmit && styles.sendBtnActive]}
            disabled={!canSubmit}
            hitSlop={4}
            accessibilityRole="button"
            accessibilityLabel="Send message"
          >
            {sending ? (
              <ActivityIndicator size="small" color={canSubmit ? "#fff" : TEXT_TER} />
            ) : (
              <Ionicons name="send" size={16} color={canSubmit ? "#fff" : TEXT_TER} />
            )}
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
      )}

      {/* Video URL modal */}
      <Modal
        visible={videoModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setVideoModalVisible(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setVideoModalVisible(false)}
        >
          <View style={styles.modalCard} onStartShouldSetResponder={() => true}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle} accessibilityRole="header">Embed Video URL</Text>
              <TouchableOpacity onPress={() => setVideoModalVisible(false)} hitSlop={8} accessibilityRole="button" accessibilityLabel="Close">
                <Ionicons name="close" size={18} color={TEXT_SEC} />
              </TouchableOpacity>
            </View>
            <Text style={styles.modalHint}>Paste a YouTube, Twitch, or other video link</Text>
            <TextInput
              style={styles.modalInput}
              value={videoUrl}
              onChangeText={setVideoUrl}
              placeholder="https://..."
              placeholderTextColor={TEXT_TER}
              autoFocus
              autoCapitalize="none"
              keyboardType="url"
              accessibilityLabel="Video link"
            />
            <View style={styles.modalFooter}>
              <TouchableOpacity onPress={() => setVideoModalVisible(false)} style={styles.modalBtnSecondary} accessibilityRole="button">
                <Text style={styles.modalBtnSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleConfirmVideoUrl}
                style={[styles.modalBtnPrimary, !videoUrl.trim() && { opacity: 0.4 }]}
                disabled={!videoUrl.trim()}
                accessibilityRole="button"
              >
                <Text style={styles.modalBtnPrimaryText}>Attach</Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: CARD_BG,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    overflow: "hidden",
    flexDirection: "column",
  },

  // ── Header ────────────────────────────────────────────────────────
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 13,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    backgroundColor: "rgba(0,0,0,0.2)",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  headerHash: {
    fontSize: 20,
    fontWeight: "700",
    color: TEXT_TER,
    lineHeight: 22,
  },
  headerName: {
    fontSize: 15,
    fontWeight: "700",
    color: TEXT_PRI,
  },
  headerTopic: { fontSize: 12, color: TEXT_TER, marginTop: 1 },
  headerBtnOn: { backgroundColor: "rgba(255,255,255,0.08)" },
  headerDot: { position: "absolute", top: 7, right: 7, width: 6, height: 6, borderRadius: 3, backgroundColor: "#34D399" },
  searchBar: { flexDirection: "row", alignItems: "center", gap: 8, paddingHorizontal: 16, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: BORDER, backgroundColor: "rgba(0,0,0,0.15)" },
  searchInput: { flex: 1, color: TEXT_PRI, fontSize: 13, paddingVertical: 4, ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}) },
  searchCount: { color: TEXT_TER, fontSize: 11 },
  loadMore: { alignSelf: "center", marginVertical: 10, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8, borderWidth: 1, borderColor: BORDER },
  loadMoreText: { color: TEXT_SEC, fontSize: 12, fontWeight: "600" },
  msgReview: { color: "#FBBF24", fontSize: 11, marginTop: 4 },
  pendingBet: { flexDirection: "row", alignItems: "flex-start", gap: 8, paddingHorizontal: 16, paddingTop: 10, borderTopWidth: 1, borderTopColor: BORDER },
  pendingLabel: { color: "#34D399", fontSize: 10, fontWeight: "800", letterSpacing: 1.5, textTransform: "uppercase" },
  signInBar: { flexDirection: "row", alignItems: "center", gap: 12, padding: 14, borderTopWidth: 1, borderTopColor: BORDER, flexWrap: "wrap" },
  signInText: { flex: 1, color: TEXT_SEC, fontSize: 13, minWidth: 180 },
  signInBtn: { paddingHorizontal: 16, paddingVertical: 9, borderRadius: 10, backgroundColor: "#F5F5FA" },
  signInBtnText: { color: "#0B0B12", fontSize: 13, fontWeight: "700" },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  headerBtn: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
  },

  // ── Messages ──────────────────────────────────────────────────────
  msgList: {
    flex: 1,
  },
  msgListInner: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 8,
  },

  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },

  // Empty state
  emptyCentered: {
    flex: 1,
    alignItems: "center",
    paddingTop: 48,
    paddingHorizontal: 24,
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    backgroundColor: "rgba(162,89,255,0.15)",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "rgba(162,89,255,0.25)",
  },
  emptyHash: {
    fontSize: 32,
    fontWeight: "800",
    color: BRAND,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: TEXT_PRI,
    marginBottom: 8,
    textAlign: "center",
  },
  emptyBody: {
    fontSize: 14,
    color: TEXT_TER,
    textAlign: "center",
    lineHeight: 20,
  },

  // Day separator
  daySep: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 16,
    gap: 10,
  },
  daySepLine: {
    flex: 1,
    height: 1,
    backgroundColor: BORDER,
  },
  daySepText: {
    fontSize: 11,
    fontWeight: "600",
    color: TEXT_TER,
    letterSpacing: 0.3,
  },

  // Message row
  msgRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 1,
  },
  msgRowFirst: {
    marginTop: 10,
  },
  msgAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    flexShrink: 0,
  },
  msgAvatarText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "700",
  },
  msgGutter: {
    width: 36,
    marginRight: 10,
    flexShrink: 0,
  },
  msgGutterTime: {
    width: 36,
    marginRight: 10,
    flexShrink: 0,
    fontSize: 9,
    color: TEXT_TER,
    textAlign: "center",
    alignSelf: "center",
    lineHeight: 14,
  },
  msgContent: {
    flex: 1,
  },
  msgHeader: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 8,
    marginBottom: 2,
  },
  msgUsername: {
    fontSize: 14,
    fontWeight: "600",
  },
  msgTime: {
    fontSize: 10,
    color: TEXT_TER,
  },
  msgText: {
    fontSize: 14,
    color: TEXT_PRI,
    lineHeight: 20,
  },
  msgLink: {
    color: BRAND,
    textDecorationLine: "underline",
  },

  // ── Notice bar ────────────────────────────────────────────────────
  noticeBar: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 6,
    backgroundColor: "rgba(0,0,0,0.3)",
  },
  noticeTextError: {
    fontSize: 12,
    color: "#f87171",
  },
  noticeTextOk: {
    fontSize: 12,
    color: "#4ade80",
  },

  // ── Chat bar ──────────────────────────────────────────────────────
  chatBar: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: BORDER,
    backgroundColor: "rgba(0,0,0,0.2)",
  },
  attachBtn: {
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    marginBottom: 1,
    ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
  },
  inputWrap: {
    flex: 1,
    backgroundColor: INPUT_BG,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.1)",
    flexDirection: "row",
    alignItems: "flex-end",
    paddingLeft: 14,
    paddingRight: 8,
    paddingVertical: 2,
    minHeight: 40,
  },
  textInput: {
    flex: 1,
    fontSize: 14,
    color: TEXT_PRI,
    paddingVertical: 9,
    maxHeight: 120,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  inputActions: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 8,
    gap: 2,
  },
  inputActionBtn: {
    padding: 4,
    borderRadius: 6,
    ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
  },
  sendBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.08)",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 1,
    ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
  },
  sendBtnActive: {
    backgroundColor: BRAND,
  },

  // ── Video modal ───────────────────────────────────────────────────
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.7)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  modalCard: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#1a1a1a",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.1)",
    padding: 20,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: TEXT_PRI,
  },
  modalHint: {
    fontSize: 13,
    color: TEXT_SEC,
    marginBottom: 12,
    lineHeight: 18,
  },
  modalInput: {
    backgroundColor: INPUT_BG,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.12)",
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: TEXT_PRI,
    fontSize: 14,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  modalFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 16,
  },
  modalBtnSecondary: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.12)",
  },
  modalBtnSecondaryText: {
    color: TEXT_SEC,
    fontSize: 14,
    fontWeight: "500",
  },
  modalBtnPrimary: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: BRAND,
  },
  modalBtnPrimaryText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "600",
  },
});
